/**
 * Option Chain Page with Real-time Data
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import aliceBlueWS from '../services/aliceBlueWebSocket';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import OrderPanel from '../components/OrderPanel';
import StrikeChart from '../components/StrikeChart';
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts';
import ShortcutInfoDrawer from '../components/Shortcuts/ShortcutInfoDrawer';

const OptionChain = () => {
  const { userId, userSession } = useSelector((state) => state.auth);
  const { theme } = useTheme();
  
  // WebSocket state
  const [livePrices, setLivePrices] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  const [wsStatus, setWsStatus] = useState('Initializing...');
  
  // Option Chain data
  const [underlyingList, setUnderlyingList] = useState([]);
  const [selectedUnderlying, setSelectedUnderlying] = useState('');
  const [expiryList, setExpiryList] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState('');
  const [selectedInterval, setSelectedInterval] = useState('10');
  const [selectedExchange, setSelectedExchange] = useState('nse_fo');
  const [optionChainData, setOptionChainData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Order Panel state
  const [isOrderPanelOpen, setIsOrderPanelOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  
  // Spot price tracking
  const [spotPrice, setSpotPrice] = useState(null);
  
  // Chart data state
  const [selectedStrikes, setSelectedStrikes] = useState([]);
  const [chartData, setChartData] = useState({});
  const [loadingChart, setLoadingChart] = useState(false);

  // Initialize WebSocket
  useEffect(() => {
    let mounted = true;

    const handlePriceUpdate = (priceData) => {
      if (!mounted) return;
      const key = `${priceData.exchange}_${priceData.token}`;
      setLivePrices(prev => {
        // Only update if the value actually changed
        if (prev[key]?.ltp === priceData.ltp) return prev;
        return {
          ...prev,
          [key]: priceData
        };
      });
    };

    const initWebSocket = async () => {
      try {
        setWsStatus('Connecting...');
        await aliceBlueWS.connect();
        
        if (!mounted) return;
        
        setWsConnected(true);
        setWsStatus('Connected');
        
        // Add subscriber for price updates (wildcard for all symbols)
        aliceBlueWS.addSubscriber('*', handlePriceUpdate);

      } catch (error) {
        console.error('WebSocket connection failed:', error);
        if (mounted) {
          setWsStatus(`Error: ${error.message}`);
          setWsConnected(false);
        }
      }
    };

    initWebSocket();

    return () => {
      mounted = false;
      aliceBlueWS.removeSubscriber('*', handlePriceUpdate);
    };
  }, []);

  // Fetch underlying list on mount
  useEffect(() => {
    fetchUnderlyingList();
  }, [selectedExchange]);

  // Fetch expiry list when underlying changes
  useEffect(() => {
    if (selectedUnderlying) {
      fetchExpiryList();
    }
  }, [selectedUnderlying]);

  // Fetch option chain when all parameters are selected
  useEffect(() => {
    if (selectedUnderlying && selectedExpiry && selectedInterval) {
      fetchOptionChain();
    }
  }, [selectedUnderlying, selectedExpiry, selectedInterval]);

  // Subscribe to WebSocket for option chain tokens
  useEffect(() => {
    if (optionChainData.length > 0 && wsConnected) {
      const instruments = [];
      
      optionChainData.forEach(item => {
        if (item.CE && item.CE.token) {
          instruments.push({
            exchange: selectedExchange === 'nse_fo' ? 'NFO' : selectedExchange === 'bse_fo' ? 'BFO' : 'MCX',
            token: item.CE.token
          });
        }
        if (item.PE && item.PE.token) {
          instruments.push({
            exchange: selectedExchange === 'nse_fo' ? 'NFO' : selectedExchange === 'bse_fo' ? 'BFO' : 'MCX',
            token: item.PE.token
          });
        }
      });

      if (instruments.length > 0) {
        console.log('📡 Subscribing to', instruments.length, 'option instruments');
        aliceBlueWS.subscribe(instruments);
      }
    }
  }, [optionChainData, wsConnected, selectedExchange]);

  const fetchUnderlyingList = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/option-chain/underlying', {
        exch: selectedExchange,
        userSession
      });

      if (response.data.success && response.data.data.result) {
        const list = response.data.data.result[0]?.list_underlying || [];
        setUnderlyingList(list);
        if (list.length > 0 && !selectedUnderlying) {
          setSelectedUnderlying(list[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching underlying list:', error);
      setError('Failed to fetch underlying list');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpiryList = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/option-chain/expiry', {
        underlying: selectedUnderlying,
        exch: selectedExchange,
        userSession
      });

      if (response.data.success && response.data.data.result) {
        const list = response.data.data.result[0]?.underlying_expiry || [];
        setExpiryList(list);
        if (list.length > 0 && !selectedExpiry) {
          setSelectedExpiry(list[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching expiry list:', error);
      setError('Failed to fetch expiry list');
    } finally {
      setLoading(false);
    }
  };

  const fetchOptionChain = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/option-chain/chain', {
        underlying: selectedUnderlying,
        expiry: selectedExpiry,
        interval: selectedInterval,
        exch: selectedExchange,
        userSession
      });

      if (response.data.success && response.data.data.result) {
        const data = response.data.data.result[0]?.data || [];
        setOptionChainData(data);
      }
    } catch (error) {
      console.error('Error fetching option chain:', error);
      setError('Failed to fetch option chain data');
    } finally {
      setLoading(false);
    }
  };

  const getLivePrice = (token) => {
    const exchange = selectedExchange === 'nse_fo' ? 'NFO' : selectedExchange === 'bse_fo' ? 'BFO' : 'MCX';
    const key = `${exchange}_${token}`;
    return livePrices[key]?.ltp || null;
  };

  const getStrikePrice = (item) => {
    // First check if there's a direct CE or PE option data
    const option = item.CE || item.PE;
    if (!option) return null;
    
    // Check if forInsName contains the strike price (format: "NIFTY 27th JAN 23000 CE")
    if (option.forInsName) {
      const match = option.forInsName.match(/(\d+(?:\.\d+)?)\s+(CE|PE|CALL|PUT)/i);
      if (match) {
        return parseFloat(match[1]);
      }
    }
    
    // Try to extract from tradingsymbol
    if (option.tradingsymbol) {
      const symbol = option.tradingsymbol;
      
      // Pattern 1: Numbers before CE/PE at the end (e.g., NIFTY27JAN2623000CE -> 23000)
      let match = symbol.match(/(\d+)(CE|PE)$/i);
      if (match) {
        return parseFloat(match[1]);
      }
      
      // Pattern 2: Decimal strikes (e.g., EURUSD26NOV25C1.1 -> 1.1)
      match = symbol.match(/[CP](\d+(?:\.\d+)?)$/i);
      if (match) {
        return parseFloat(match[1]);
      }
    }
    
    return null;
  };

  const handleTrade = (option, type) => {
    if (!option || !option.token) return;
    
    const ltp = getLivePrice(option.token);
    const exchange = selectedExchange === 'nse_fo' ? 'NFO' : selectedExchange === 'bse_fo' ? 'BFO' : 'MCX';
    
    setSelectedSymbol({
      symbol: option.tradingsymbol,
      name: option.forInsName || option.tradingsymbol,
      exchange: exchange,
      instrumentId: option.token,
      ltp: ltp || parseFloat(option.ltp) || 0,
      orderType: type
    });
    setIsOrderPanelOpen(true);
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    const value = parseFloat(num);
    if (isNaN(value)) return '-';
    return value.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatOI = (num) => {
    if (!num) return '-';
    const value = parseFloat(num);
    if (isNaN(value)) return '-';
    if (value >= 10000000) return (value / 10000000).toFixed(2) + 'Cr';
    if (value >= 100000) return (value / 100000).toFixed(2) + 'L';
    if (value >= 1000) return (value / 1000).toFixed(2) + 'K';
    return value.toFixed(0);
  };

  const getATMStrike = () => {
    if (!spotPrice || optionChainData.length === 0) return null;
    
    let closestStrike = null;
    let minDiff = Infinity;
    
    optionChainData.forEach(item => {
      const strike = getStrikePrice(item);
      if (strike) {
        const diff = Math.abs(strike - spotPrice);
        if (diff < minDiff) {
          minDiff = diff;
          closestStrike = strike;
        }
      }
    });
    
    return closestStrike;
  };

  const atmStrike = getATMStrike();

  // Fetch historical chart data
  const fetchChartData = async (option) => {
    if (!option || !option.token) return;
    
    const key = `${option.token}_${option.tradingsymbol}`;
    
    // Check if already loaded
    if (chartData[key]) {
      return;
    }
    
    try {
      setLoadingChart(true);
      
      const exchange = selectedExchange === 'nse_fo' ? 'NFO' : selectedExchange === 'bse_fo' ? 'BFO' : 'MCX';
      
      // Get last 30 days data
      // AliceBlue API expects timestamps in milliseconds (UNIX timestamp)
      const to = Date.now();
      const from = Date.now() - (30 * 24 * 60 * 60 * 1000);
      
      console.log('DEBUG: Timestamp values - from:', from, 'to:', to);
      
      const payload = {
        token: option.token,
        resolution: 'D', // Daily data
        from: from.toString(),
        to: to.toString(),
        exchange: exchange,
        userSession: userSession
      };
      
      console.log('Fetching chart data with payload:', { ...payload, userSession: '***' });
      
      const response = await api.post('/chart/history', payload);
      
      if (response.data.success && response.data.data.result) {
        setChartData(prev => ({
          ...prev,
          [key]: {
            data: response.data.data.result,
            option: option
          }
        }));
      } else {
        console.error('Chart API returned unsuccessful response:', response.data);
        const message = response.data.message || 'Failed to load chart data';
        const suggestion = response.data.details?.suggestion || '';
        
        // Show user-friendly notification
        alert(`📊 Chart Feature Not Available\n\n${message}\n\n💡 ${suggestion}`);
        
        // Don't add chart panel if feature is not available
        return;
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      console.error('Error response:', error.response?.data);
      
      // Check for market hours restriction (405 or specific error message)
      const errorData = error.response?.data;
      const is405Error = error.response?.status === 405;
      const isMarketHoursError = errorData?.error?.includes('market') || 
                                  errorData?.message?.includes('market hours') ||
                                  errorData?.details?.info?.includes('market hours');
      
      if (is405Error || isMarketHoursError) {
        const schedule = errorData?.details?.schedule || 
                        '• 5:30 PM - 8:00 AM on weekdays\n• Fully available on weekends and holidays';
        alert(`⏰ Market Hours Restriction\n\nHistorical chart data is only available after market hours.\n\n📅 Available:\n${schedule}\n\nPlease try again after 5:30 PM.`);
      } else {
        const errorMsg = errorData?.error || errorData?.message || error.message;
        alert(`Failed to load chart: ${errorMsg}`);
      }
    } finally {
      setLoadingChart(false);
    }
  };
  
  // Handle double-click on strike row
  const handleStrikeDoubleClick = (option) => {
    if (!option || !option.token) return;
    
    const key = `${option.token}_${option.tradingsymbol}`;
    
    // Check if already selected
    const isSelected = selectedStrikes.includes(key);
    
    if (isSelected) {
      // Remove from selected strikes
      setSelectedStrikes(prev => prev.filter(k => k !== key));
    } else {
      // Add to selected strikes (max 3 charts)
      if (selectedStrikes.length >= 3) {
        alert('Maximum 3 charts can be displayed at once');
        return;
      }
      setSelectedStrikes(prev => [...prev, key]);
      fetchChartData(option);
    }
  };
  
  // Clear all charts
  const clearAllCharts = () => {
    setSelectedStrikes([]);
  };

  // ==================== KEYBOARD SHORTCUT HANDLERS ====================
  const shortcutActionHandlers = {
    // Option chain buy/sell - uses ATM strike data if available
    buy_ce: () => {
      const atmItem = optionChainData.find(item => item.isATM);
      if (atmItem?.CE) handleTrade(atmItem.CE, 'BUY');
    },
    sell_ce: () => {
      const atmItem = optionChainData.find(item => item.isATM);
      if (atmItem?.CE) handleTrade(atmItem.CE, 'SELL');
    },
    buy_pe: () => {
      const atmItem = optionChainData.find(item => item.isATM);
      if (atmItem?.PE) handleTrade(atmItem.PE, 'BUY');
    },
    sell_pe: () => {
      const atmItem = optionChainData.find(item => item.isATM);
      if (atmItem?.PE) handleTrade(atmItem.PE, 'SELL');
    },
  };

  const { shortcutsEnabled, setShortcutsEnabled, shortcuts, lastTriggered } = useKeyboardShortcuts('option-chain', shortcutActionHandlers);
  // ==================== END KEYBOARD SHORTCUT HANDLERS ====================

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold">Option Chain</h1>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Real-time option chain data with live market prices
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShortcutsEnabled(!shortcutsEnabled)}
                className={`px-2 py-1 rounded text-xs font-medium border transition-all duration-200 ${
                  shortcutsEnabled
                    ? 'bg-purple-600 text-white border-purple-600 shadow-sm shadow-purple-200'
                    : theme === 'dark'
                      ? 'bg-gray-800 text-gray-400 border-gray-600 hover:border-purple-400 hover:text-purple-400'
                      : 'bg-white text-gray-500 border-gray-300 hover:border-purple-400 hover:text-purple-600'
                }`}
                title={shortcutsEnabled ? 'Keyboard shortcuts active - Click to disable' : 'Enable keyboard shortcuts'}
              >
                ⌨️ {shortcutsEnabled ? 'Shortcuts ON' : 'Shortcuts'}
              </button>
              <ShortcutInfoDrawer shortcuts={shortcuts} variant={theme === 'dark' ? 'dark' : 'light'} />
              {lastTriggered && (Date.now() - lastTriggered.timestamp < 2000) && (
                <div className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded animate-pulse">
                  ⌨️ {lastTriggered.key} → {lastTriggered.action}
                </div>
              )}
              <div className={`px-3 py-1 rounded-full text-sm ${
                wsConnected 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }`}>
                {wsStatus}
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className={`p-6 rounded-lg mb-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Exchange Selection */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Exchange
              </label>
              <select
                value={selectedExchange}
                onChange={(e) => {
                  setSelectedExchange(e.target.value);
                  setSelectedUnderlying('');
                  setSelectedExpiry('');
                  setOptionChainData([]);
                }}
                className={`w-full px-3 py-2 rounded-lg border ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value="nse_fo">NSE F&O</option>
                <option value="bse_fo">BSE F&O</option>
                <option value="mcx_fo">MCX F&O</option>
              </select>
            </div>

            {/* Underlying Selection */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Underlying
              </label>
              <select
                value={selectedUnderlying}
                onChange={(e) => {
                  setSelectedUnderlying(e.target.value);
                  setSelectedExpiry('');
                  setOptionChainData([]);
                }}
                disabled={loading || underlyingList.length === 0}
                className={`w-full px-3 py-2 rounded-lg border ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50`}
              >
                <option value="">Select Underlying</option>
                {underlyingList.map(underlying => (
                  <option key={underlying} value={underlying}>{underlying}</option>
                ))}
              </select>
            </div>

            {/* Expiry Selection */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Expiry
              </label>
              <select
                value={selectedExpiry}
                onChange={(e) => {
                  setSelectedExpiry(e.target.value);
                  setOptionChainData([]);
                }}
                disabled={loading || expiryList.length === 0}
                className={`w-full px-3 py-2 rounded-lg border ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50`}
              >
                <option value="">Select Expiry</option>
                {expiryList.map(expiry => (
                  <option key={expiry} value={expiry}>{expiry}</option>
                ))}
              </select>
            </div>

            {/* Interval Selection */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Strike Interval
              </label>
              <select
                value={selectedInterval}
                onChange={(e) => {
                  setSelectedInterval(e.target.value);
                  setOptionChainData([]);
                }}
                className={`w-full px-3 py-2 rounded-lg border ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
                <option value="25">25</option>
              </select>
            </div>

            {/* Refresh Button */}
            <div className="flex items-end">
              <button
                onClick={fetchOptionChain}
                disabled={loading || !selectedUnderlying || !selectedExpiry}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg dark:bg-red-900 dark:border-red-700 dark:text-red-200">
            {error}
          </div>
        )}

        {/* Main Container with Table and Right Panel */}
        <div className="flex gap-6">
          {/* Option Chain Table - Left Side (30% width) */}
          {optionChainData.length > 0 && (
            <div className="w-full lg:w-[30%]">
              <div className={`rounded-lg overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
                <div className="overflow-x-auto">
                  <table className="w-full">
                <thead className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <tr>
                    {/* Call Options */}
                    <th className="px-1 py-1 text-center border-r border-gray-600" colSpan="5">
                      <span className="text-green-600 dark:text-green-400 font-bold text-xs">CALL</span>
                    </th>
                    
                    {/* Strike Price */}
                    <th className="px-1 py-1 text-center bg-blue-100 dark:bg-blue-900 font-bold">
                      <span className="text-blue-600 dark:text-blue-300 text-xs">STRIKE</span>
                    </th>
                    
                    {/* Put Options */}
                    <th className="px-1 py-1 text-center border-l border-gray-600" colSpan="5">
                      <span className="text-red-600 dark:text-red-400 font-bold text-xs">PUT</span>
                    </th>
                  </tr>
                  <tr className={`text-[10px] ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {/* CE Headers */}
                    <th className="px-1 py-1 text-center">OI</th>
                    <th className="px-1 py-1 text-center">ΔOI</th>
                    <th className="px-1 py-1 text-center">LTP</th>
                    <th className="px-1 py-1 text-center">Δ</th>
                    <th className="px-1 py-1 text-center border-r border-gray-600">B/S</th>
                    
                    {/* Strike */}
                    <th className="px-1 py-1 text-center bg-blue-100 dark:bg-blue-900"></th>
                    
                    {/* PE Headers */}
                    <th className="px-1 py-1 text-center border-l border-gray-600">B/S</th>
                    <th className="px-1 py-1 text-center">Δ</th>
                    <th className="px-1 py-1 text-center">LTP</th>
                    <th className="px-1 py-1 text-center">ΔOI</th>
                    <th className="px-1 py-1 text-center">OI</th>
                  </tr>
                </thead>
                <tbody>
                  {optionChainData.map((item, index) => {
                    const strikePrice = getStrikePrice(item);
                    const isATM = strikePrice === atmStrike;
                    const ceLTP = item.CE ? (getLivePrice(item.CE.token) || parseFloat(item.CE.ltp) || 0) : 0;
                    const peLTP = item.PE ? (getLivePrice(item.PE.token) || parseFloat(item.PE.ltp) || 0) : 0;
                    const ceChange = item.CE ? (ceLTP - parseFloat(item.CE.pdc || 0)) : 0;
                    const peChange = item.PE ? (peLTP - parseFloat(item.PE.pdc || 0)) : 0;
                    
                    return (
                      <tr 
                        key={`${item.CE?.token || ''}-${item.PE?.token || ''}-${index}`}
                        className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
                          isATM ? 'bg-yellow-50 dark:bg-yellow-900 dark:bg-opacity-20' : ''
                        }`}
                        onDoubleClick={() => {
                          // Default to CE, but if user double-clicks on PE side, use PE
                          handleStrikeDoubleClick(item.CE || item.PE);
                        }}
                        title="Double-click to view chart"
                      >
                        {/* CE Data */}
                        <td className="px-1 py-1 text-center text-[10px]">{formatOI(item.CE?.oi)}</td>
                        <td className={`px-1 py-1 text-center text-[10px] ${
                          parseFloat(item.CE?.oi || 0) - parseFloat(item.CE?.pdoi || 0) > 0 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatOI(parseFloat(item.CE?.oi || 0) - parseFloat(item.CE?.pdoi || 0))}
                        </td>
                        <td className="px-1 py-1 text-center text-[11px] font-semibold">{formatNumber(ceLTP)}</td>
                        <td className={`px-1 py-1 text-center text-[10px] ${
                          ceChange > 0 ? 'text-green-600 dark:text-green-400' : ceChange < 0 ? 'text-red-600 dark:text-red-400' : ''
                        }`}>
                          {ceChange > 0 ? '+' : ''}{formatNumber(ceChange)}
                        </td>
                        <td className="px-1 py-1 text-center border-r border-gray-600">
                          {item.CE && (
                            <div className="flex gap-0.5 justify-center">
                              <button
                                onClick={() => handleTrade(item.CE, 'BUY')}
                                className="px-1 py-0.5 text-[9px] bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                title={`Buy ${item.CE.tradingsymbol}`}
                              >
                                B
                              </button>
                              <button
                                onClick={() => handleTrade(item.CE, 'SELL')}
                                className="px-1 py-0.5 text-[9px] bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                                title={`Sell ${item.CE.tradingsymbol}`}
                              >
                                S
                              </button>
                            </div>
                          )}
                        </td>
                        
                        {/* Strike Price */}
                        <td className={`px-1 py-1 text-center font-bold text-xs bg-blue-50 dark:bg-blue-900 dark:bg-opacity-30 ${
                          isATM ? 'text-yellow-600 dark:text-yellow-400' : 'text-blue-600 dark:text-blue-300'
                        }`}>
                          {strikePrice ? formatNumber(strikePrice, 0) : '-'}
                          {isATM && <span className="ml-0.5 text-[9px]">ATM</span>}
                        </td>
                        
                        {/* PE Data */}
                        <td className="px-1 py-1 text-center border-l border-gray-600">
                          {item.PE && (
                            <div className="flex gap-0.5 justify-center">
                              <button
                                onClick={() => handleTrade(item.PE, 'BUY')}
                                className="px-1 py-0.5 text-[9px] bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                title={`Buy ${item.PE.tradingsymbol}`}
                              >
                                B
                              </button>
                              <button
                                onClick={() => handleTrade(item.PE, 'SELL')}
                                className="px-1 py-0.5 text-[9px] bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                                title={`Sell ${item.PE.tradingsymbol}`}
                              >
                                S
                              </button>
                            </div>
                          )}
                        </td>
                        <td className={`px-1 py-1 text-center text-[10px] ${
                          peChange > 0 ? 'text-green-600 dark:text-green-400' : peChange < 0 ? 'text-red-600 dark:text-red-400' : ''
                        }`}>
                          {peChange > 0 ? '+' : ''}{formatNumber(peChange)}
                        </td>
                        <td className="px-1 py-1 text-center text-[11px] font-semibold">{formatNumber(peLTP)}</td>
                        <td className={`px-1 py-1 text-center text-[10px] ${
                          parseFloat(item.PE?.oi || 0) - parseFloat(item.PE?.pdoi || 0) > 0 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatOI(parseFloat(item.PE?.oi || 0) - parseFloat(item.PE?.pdoi || 0))}
                        </td>
                        <td className="px-1 py-1 text-center text-[10px]">{formatOI(item.PE?.oi)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        )}
        
        {/* Right Panel - Chart Analysis */}
        {optionChainData.length > 0 && (
          <div className="flex-1">
            <div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Strike Analysis
                </h3>
                {selectedStrikes.length > 0 && (
                  <button
                    onClick={clearAllCharts}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Clear All
                  </button>
                )}
              </div>
              
              {selectedStrikes.length === 0 ? (
                <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  <p className="text-sm mb-2">📊 Double-click on any strike row to view its chart</p>
                  <p className="text-xs">(Maximum 3 charts can be displayed)</p>
                  <p className="text-xs mt-4 text-yellow-600 dark:text-yellow-400">
                    ⚠️ Historical data available: 5:30 PM - 8 AM on weekdays, fully on weekends/holidays
                  </p>
                </div>
              ) : (
                <div className="space-y-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
                  {loadingChart && (
                    <div className="text-center py-4">
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Loading chart data...
                      </p>
                    </div>
                  )}
                  {selectedStrikes.map(key => {
                    const chart = chartData[key];
                    if (!chart) return null;
                    
                    return (
                      <div key={key} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h4 className={`text-sm font-semibold ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                              {chart.option.forInsName || chart.option.tradingsymbol}
                            </h4>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              Token: {chart.option.token}
                            </p>
                          </div>
                          <button
                            onClick={() => setSelectedStrikes(prev => prev.filter(k => k !== key))}
                            className="text-red-600 hover:text-red-700 text-xl"
                            title="Remove chart"
                          >
                            ×
                          </button>
                        </div>
                        <StrikeChart 
                          data={chart.data} 
                          title={chart.option.tradingsymbol}
                          theme={theme}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

        {/* Empty State */}
        {!loading && optionChainData.length === 0 && selectedUnderlying && selectedExpiry && (
          <div className={`text-center py-12 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No option chain data available. Please select an underlying and expiry.
            </p>
          </div>
        )}
      </div>

      {/* Order Panel */}
      {isOrderPanelOpen && selectedSymbol && (
        <OrderPanel
          symbol={selectedSymbol}
          isOpen={isOrderPanelOpen}
          onClose={() => {
            setIsOrderPanelOpen(false);
            setSelectedSymbol(null);
          }}
        />
      )}
    </div>
  );
};

export default OptionChain;
